import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { UserIdSchema, VoiceIdSchema, VoiceReplyIdSchema } from "../id"

// ─── UserFavoriteDocument ────────────────────────────────────────────────────
// Path: user-privates/{userId}/user-favorites/{entryId}
//
// ユーザーのお気に入り登録。kind で種別を管理し、将来的に
// voice 以外のリソースも追加可能。

export const UserFavoriteKindSchema = z.enum([
  "voice",
  "voice-reply",
])

export type UserFavoriteKind = z.infer<typeof UserFavoriteKindSchema>

export const UserFavoriteVoiceTarget = z.object({
  kind: z.literal("voice"),
  voice_owner_user_id: UserIdSchema.describe("ボイス投稿者のユーザーID"),
  voice_id: VoiceIdSchema.describe("ボイス投稿のID"),
})

export type UserFavoriteVoiceTarget = z.infer<typeof UserFavoriteVoiceTarget>

export const UserFavoriteVoiceReplyTarget = z.object({
  kind: z.literal("voice-reply"),
  voice_owner_user_id: UserIdSchema.describe("ボイス投稿者のユーザーID"),
  voice_id: VoiceIdSchema.describe("ボイス投稿のID"),
  voice_reply_id: VoiceReplyIdSchema.describe("ボイス返信のID"),
})

export type UserFavoriteVoiceReplyTarget = z.infer<typeof UserFavoriteVoiceReplyTarget>

export const UserFavoriteTarget = z.discriminatedUnion("kind", [
  UserFavoriteVoiceTarget,
  UserFavoriteVoiceReplyTarget,
])

export type UserFavoriteTarget = z.infer<typeof UserFavoriteTarget>

export const UserFavoriteDocumentMeta = z.object({
  target: UserFavoriteTarget.describe("お気に入り対象"),
})

export type UserFavoriteDocumentMeta = z.infer<typeof UserFavoriteDocumentMeta>

export const UserFavoriteDocumentBody = z.intersection(
  UserFavoriteDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type UserFavoriteDocumentBody = z.infer<typeof UserFavoriteDocumentBody>

export const UserFavoriteDocument = z.intersection(
  UserFavoriteDocumentBody,
  z.object({
    id: z.string().describe("お気に入りエントリのID（自動生成）"),
  })
)

export type UserFavoriteDocument = z.infer<typeof UserFavoriteDocument>

export class UserFavoriteDocuments {

  static toDocumentData(entry: UserFavoriteDocument): object {
    return {
      ...entry,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): UserFavoriteDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: doc.id,
      target: data["target"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
